/* eslint-disable no-console, class-methods-use-this, no-unused-vars */
import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import {
  recordTransportChange,
  trackDeliveries,
} from 'dashboard/helper/realtimeDelivery';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.beatPresenceOnServerPing();

    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'team.changed': this.onTeamChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'notification.read_all': this.onNotificationReadAll,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'account.settings_changed': this.onAccountSettingsChanged,
      'copilot.message.created': this.onCopilotMessageCreated,
      campaign_status_updated: this.onCampaignStatusUpdated,
      campaign_deleted: this.onCampaignDeleted,

      // EVENTOS BAILEYS
      'baileys.connection.connected': this.onBaileysConnected,
      'baileys.connection.disconnected': this.onBaileysDisconnected,
      'baileys.connection.qr_generated': this.onBaileysQRGenerated,
      'baileys.connection.pairing_code_generated':
        this.onBaileysPairingCodeGenerated,
      'baileys.history_import_completed': this.onBaileysHistoryImportCompleted,

      // AGENT EVENTS
      'agent.updated': this.onAgentUpdated,

      // CALL RECORD EVENTS
      'call_record.created': this.onCallRecordCreated,

      // TELEPHONY TRANSFER EVENTS
      'telephony.transfer.completed': this.onTelephonyTransferCompleted,

      // IVR AUDIO EVENTS
      'ivr_audio.updated': this.onIvrAudioUpdated,

      // VOICE AI EVENTS
      'ai.document.updated': this.onStudioDocumentUpdated,
      'ai.session.event': this.onAiSessionEvent,

      // CHAMADOS DE SUPORTE
      'chamado.event': this.onChamadoEvent,

      // CENTRAL DE AVISOS (OpsNotice) — aviso aberto/somado/fechado
      'ops_notice.created': this.onOpsNoticeChanged,
      'ops_notice.updated': this.onOpsNoticeChanged,

      // MASSIVA (queda coletiva de rede) — transições do poll
      'massiva.event': this.onMassivaEvent,

      // WHATSAPP TEMPLATE EVENTS
      'whatsapp.template.status_updated': this.onWhatsAppTemplateStatusUpdated,
      'custom_attribute_definition.changed':
        this.onCustomAttributeDefinitionChanged,

      // AUTODIALER EVENTS
      'autodialer.event': this.onAutoDialerEvent,

      // KANBAN EVENTS
      'kanban.event': this.onKanbanEvent,
      'meta_costs.updated': this.onMetaCostsUpdated,

      // WHATSAPP SIP EXTENSION PRESENCE (AMI ContactStatus via NATS)
      'whatsapp_sip.extension_presence': this.onWhatsAppSipExtensionPresence,

      // ASTERISK BACKGROUND EVENTS (jobs Sidekiq dispatched from controllers)
      'asterisk.extension.changed': this.onAsteriskExtensionChanged,
      'asterisk.did.changed': this.onAsteriskDidChanged,
      'asterisk.queue.changed': this.onAsteriskQueueChanged,
      'asterisk.trunk.changed': this.onAsteriskTrunkChanged,
      'asterisk.ivr.changed': this.onAsteriskIvrChanged,
      'asterisk.ivr_flow.changed': this.onAsteriskIvrFlowChanged,
      'asterisk.queue_route.changed': this.onAsteriskQueueRouteChanged,
      // Desfecho do Asterisk::CdrReconcileJob (botão "Atualizar do CDR")
      'call_records.reconcile.finished': this.onCallRecordsReconcileFinished,

      // INBOX MEMBERSHIP — refetch sidebar quando user e adicionado/removido
      'inbox.membership_changed': this.onInboxMembershipChanged,
    };
    // Relógio de entrega por família (conversas/mensagens) que o refetch de
    // segurança lê pra detectar canal morto — ver realtimeDelivery.js.
    this.events = trackDeliveries(this.events);
  }

  onBaileysConnected = data => {
    const inboxId = Number(data.inbox_id);
    this.app.$store.dispatch('inboxes/setBaileysConnectionStatus', {
      inboxId,
      status: { status: 'CONNECTED', phone_number: data.phone_number },
    });
    emitter.emit(BUS_EVENTS.BAILEYS_CONNECTED, { ...data, inbox_id: inboxId });
  };

  onBaileysDisconnected = data => {
    const inboxId = Number(data.inbox_id);
    this.app.$store.dispatch('inboxes/setBaileysConnectionStatus', {
      inboxId,
      status: { status: 'DISCONNECTED', reason: data.reason },
    });
    emitter.emit(BUS_EVENTS.BAILEYS_DISCONNECTED, {
      ...data,
      inbox_id: inboxId,
    });
  };

  onBaileysQRGenerated = data => {
    emitter.emit(BUS_EVENTS.BAILEYS_QR_GENERATED, data);
  };

  onBaileysPairingCodeGenerated = data => {
    emitter.emit(BUS_EVENTS.BAILEYS_PAIRING_CODE_GENERATED, data);
  };

  onBaileysHistoryImportCompleted = data => {
    const inboxId = Number(data.inbox_id);
    const inboxName = data.inbox_name || 'WhatsApp';
    const messagesImported = data.messages_imported || 0;

    // Emitir evento para componentes que precisam atualizar
    emitter.emit(BUS_EVENTS.BAILEYS_HISTORY_IMPORT_COMPLETED, {
      ...data,
      inbox_id: inboxId,
    });

    // Forçar refresh das conversas para garantir que todas apareçam
    this.app.$store.dispatch('fetchAllConversations');

    // Sincronizar mensagens da conversa ativa (se houver)
    const selectedChat = this.app.$store.getters.getSelectedChat;
    if (selectedChat?.id && selectedChat?.inbox_id === inboxId) {
      this.app.$store.dispatch('syncActiveConversationMessages', {
        conversationId: selectedChat.id,
      });
    }

    // Mostrar toast informando o usuário
    /*
    emitter.emit(BUS_EVENTS.SHOW_TOAST, {
      message: `Importação concluída: ${messagesImported} mensagens importadas para ${inboxName}`,
      action: { duration: 5000 },
    });
    */
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    recordTransportChange();
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    recordTransportChange();
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);

    // ✅ NOVO: Verificar se a mensagem tem status e atualizar delivery correspondente
    if (data.id && data.status && data.previous_changes?.status) {
      this.app.$store.dispatch('campaigns/updateDeliveryByMessageId', {
        messageId: data.id,
        status: data.status,
        updatedAt: data.updated_at,
      });
    }
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
    this.app.$store.dispatch('operationsDashboard/realtimeNudge', 'presence');
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
    this.app.$store.dispatch(
      'operationsDashboard/realtimeNudge',
      'conversations'
    );
  };

  onTeamChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
    this.app.$store.dispatch(
      'operationsDashboard/realtimeNudge',
      'conversations'
    );
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
    this.app.$store.dispatch(
      'operationsDashboard/realtimeNudge',
      'conversations'
    );
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
    this.app.$store.dispatch(
      'operationsDashboard/realtimeNudge',
      'conversations'
    );
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
    this.app.$store.dispatch(
      'operationsDashboard/realtimeNudge',
      'conversations'
    );
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // Recontagem dos contadores da lista. Era emitida SEM freio, e cada emissão
  // vira 1 request de /conversations/meta no ChatList — durante campanha isso
  // multiplicava por conversa criada: 19.785 conversas da conta 35 em
  // 2026-08-07 = ~19,8 mil requests POR ABA aberta (o Chrome enfileira além de
  // 6 conexões: a aba trava e o último evento, o terminal da campanha, nunca é
  // processado — foi o "Em execução" congelado). No servidor, o mesmo #meta
  // aparecia com 213s/224s nos traces.
  //
  // leading + trailing: ação isolada do atendente (resolver/atribuir) recontA
  // na hora; rajada colapsa em 1 request no início e 1 no fim da janela.
  // Contador não precisa ser exato ao milissegundo — precisa existir.
  CONVERSATION_STATS_WINDOW_MS = 2000;

  fetchConversationStats = () => {
    const now = Date.now();
    const elapsed = now - (this.lastConversationStatsAt || 0);

    if (elapsed >= this.CONVERSATION_STATS_WINDOW_MS) {
      this.lastConversationStatsAt = now;
      emitter.emit(BUS_EVENTS.FETCH_CONVERSATION_STATS);
      return;
    }

    if (this.conversationStatsTimer) return;

    this.conversationStatsTimer = setTimeout(() => {
      this.conversationStatsTimer = null;
      this.lastConversationStatsAt = Date.now();
      emitter.emit(BUS_EVENTS.FETCH_CONVERSATION_STATS);
    }, this.CONVERSATION_STATS_WINDOW_MS - elapsed);
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  // Handle agent updated event (e.g., when extension is linked/unlinked)
  onAgentUpdated = data => {
    this.app.$store.dispatch('updateCurrentUserCustomAttributes', data);
  };

  // Handle call record created event
  onCallRecordCreated = data => {
    this.app.$store.dispatch('callRecords/addRecord', data);
  };

  // Handle IVR audio updated event
  onIvrAudioUpdated = data => {
    this.app.$store.dispatch('ivrAudios/updateAudioFromWebsocket', data);
  };

  // Massiva: o poll abriu/atualizou/fechou uma queda coletiva. Só o aviso vem no
  // evento — a página de Massiva rebusca no endpoint (fonte de verdade).
  //
  // A exceção é o congelamento de público da MANUTENÇÃO programada
  // (`maintenance_audience_changed`): ele termina num job, minutos depois do
  // cadastro, e o operador pode estar com o detalhe aberto. O store funde a
  // linha (spinner → contagem, ou → "Falhou" com o erro); ele mesmo ignora o
  // evento quando a lista nem foi carregada.
  // O evento é GATILHO, não dado: a lista vem do endpoint, que filtra pelo
  // papel do usuário (agente não vê aviso endereçado ao admin).
  onOpsNoticeChanged = () => {
    this.app.$store.dispatch('opsNotices/fetch');
  };

  onMassivaEvent = payload => {
    emitter.emit(BUS_EVENTS.MASSIVA_EVENT, payload);
    if (payload?.event_type === 'maintenance_audience_changed') {
      this.app.$store.dispatch(
        'studioMassivaMaintenance/handleRealtimeEvent',
        payload
      );
    }
  };

  // Handle telephony transfer completed event
  onTelephonyTransferCompleted = data => {
    console.log('[ActionCable] 📞 Transfer completed:', data);
    emitter.emit(BUS_EVENTS.TELEPHONY_TRANSFER_COMPLETED, data);
  };

  // Handle Studio document status updates
  onStudioDocumentUpdated = data => {
    emitter.emit(BUS_EVENTS.AI_DOCUMENT_UPDATED, data);
  };

  // Handle Ai::Session realtime updates — turn novo, sessao encerrada,
  // reclamacao registrada. Vuex decide se precisa atualizar lista atual
  // e qual session row substituir.
  onAiSessionEvent = data => {
    this.app.$store.dispatch('studioAnalytics/handleRealtimeEvent', data);
    this.app.$store.dispatch('operationsDashboard/realtimeNudge', 'ai');
  };

  onChamadoEvent = data => {
    this.app.$store.dispatch('studioChamados/handleRealtimeEvent', data);
    this.app.$store.dispatch('studioChamados/refreshNews');
  };

  // Definição de atributo criada/alterada fora da tela de atributos (ex.: a IA
  // criou `cpf`/`cnpj` ao confirmar o documento) — relê a lista, senão o painel
  // do contato só mostra o atributo novo depois de F5.
  onCustomAttributeDefinitionChanged = () => {
    this.app.$store.dispatch('attributes/get');
  };

  // Handle WhatsApp template status update (APPROVED, REJECTED, etc.)
  onWhatsAppTemplateStatusUpdated = data => {
    this.app.$store.dispatch('inboxes/get');
  };

  // Handle autodialer campaign/contact status updates from Go engine
  onAutoDialerEvent = data => {
    this.app.$store.dispatch('whatsappSipAutodialer/handleRealtimeEvent', data);
  };

  // Custos com a Meta: só a tela aberta escuta (useMetaCostsReport).
  onMetaCostsUpdated = data => {
    emitter.emit(BUS_EVENTS.META_COSTS_UPDATED, data);
  };

  // Handle Kanban deal realtime updates (board sync)
  onKanbanEvent = data => {
    this.app.$store.dispatch('kanban/handleRealtimeEvent', data);
  };

  // Asterisk async writes — job dispatched do controller, ao terminar broadcasta
  // pra UI refrescar a lista (success) ou exibir toast de erro.
  onWhatsAppSipExtensionPresence = data => {
    this.app.$store.dispatch('whatsappSipExtensions/updatePresence', data);
    this.app.$store.dispatch('operationsDashboard/realtimeNudge', 'voice');
  };

  onAsteriskExtensionChanged = data => {
    this.app.$store.dispatch('asteriskExtensions/handleRealtimeEvent', data);
  };

  onAsteriskDidChanged = data => {
    this.app.$store.dispatch('asteriskDids/handleRealtimeEvent', data);
  };

  onCallRecordsReconcileFinished = data => {
    this.app.$store.dispatch('callRecords/handleReconcileEvent', data);
  };

  onAsteriskQueueChanged = data => {
    this.app.$store.dispatch('asteriskQueues/handleRealtimeEvent', data);
    emitter.emit(BUS_EVENTS.ASTERISK_QUEUE_CHANGED, data);
  };

  onAsteriskTrunkChanged = data => {
    this.app.$store.dispatch('asteriskTrunks/handleRealtimeEvent', data);
  };

  onAsteriskIvrChanged = data => {
    this.app.$store.dispatch('asteriskIvrs/handleRealtimeEvent', data);
  };

  onAsteriskIvrFlowChanged = data => {
    this.app.$store.dispatch('ivrFlows/handleRealtimeEvent', data);
  };

  onAsteriskQueueRouteChanged = data => {
    this.app.$store.dispatch('asteriskQueueRoutes/handleRealtimeEvent', data);
  };

  // Quando o admin adiciona/remove o user de uma inbox via /settings,
  // o backend (InboxMember after_commit) broadcasta no pubsub_token do user.
  // Aqui refetch a lista pra que a sidebar atualize sem F5.
  onInboxMembershipChanged = () => {
    this.app.$store.dispatch('inboxes/get');
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
    // SLA estourado: além do sino, abre o toast persistente (SlaMissedToaster).
    // O backend só cria sla_missed_* pra quem deve ser avisado (atribuído,
    // participantes, admins e, com a conversa na fila, o time que a enxerga)
    // — aqui não filtra público, só o tipo. O toast em si é só do responsável:
    // quem filtra é o SlaMissedToaster (shouldShowSlaMissedToast).
    if (data.notification?.notification_type?.startsWith('sla_missed_')) {
      emitter.emit(BUS_EVENTS.SLA_MISSED_NOTIFICATION, data.notification);
    }
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
    emitter.emit(BUS_EVENTS.NOTIFICATION_DELETED, data.notification);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
    emitter.emit(BUS_EVENTS.NOTIFICATION_UPDATED, data.notification);
  };

  // Leitura pelo sino (uma conversa ou "marcar tudo"): o backend faz
  // update_all, que não gera notification.updated — vem este evento único.
  // Só fecha toast de SLA; o sino das outras abas segue como antes (sem
  // recontagem aqui).
  onNotificationReadAll = data => {
    emitter.emit(BUS_EVENTS.NOTIFICATIONS_READ_ALL, {
      primaryActorType: data.primary_actor_type,
      primaryActorId: data.primary_actor_id,
    });
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  // Conta mudou fora desta tela (feature no super admin, credencial de IA no
  // Studio): relê a conta pra os gates (Vision, resumo…) reagirem sem F5.
  onAccountSettingsChanged = () => {
    this.app.$store.dispatch('accounts/get');
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onCampaignStatusUpdated = data => {
    // ✅ EMITIR EVENTO PARA useCampaignProgress
    emitter.emit(BUS_EVENTS.CAMPAIGN_STATUS_UPDATED, data);

    // ✅ ATUALIZAR STORE COM DADOS COMPLETOS
    if (data.schedule?.id && data.message) {
      // ✅ NOVO: Detectar limite pela mensagem
      const messageText = (data.message.message || '').toLowerCase();
      const hasLimitReached =
        messageText.includes('limite') && messageText.includes('atingido');

      const statusDataForStore = {
        // status é CRU (enum); status_label é o rótulo traduzido pelo servidor.
        // Campos ausentes seguem undefined DE PROPÓSITO: nem todo broadcast
        // traz items_* (o de erro do batch manda só progress/items_failed) e
        // o `|| 0` antigo zerava o registro no store — o updateCampaignStatus
        // (assignIfPresent) só grava o que veio de fato.
        status: data.status,
        status_label: data.status_label,
        message: data.message.message,
        progress: data.message.progress,
        total_items: data.message.total_items,
        items_processed:
          data.message.items_processed ?? data.message.items_sent,
        items_sent: data.message.items_sent,
        items_failed: data.message.items_failed,
        current_batch: data.message.current_batch,
        total_batches: data.message.total_batches,
        limit_reached: hasLimitReached, // ✅ ADICIONAR
        statistics: data.message.statistics || null,
        last_updated_at: new Date().toISOString(),
      };

      this.app.$store.dispatch('campaigns/updateCampaignStatus', {
        scheduleId: data.schedule.id,
        status: data.status,
        statusLabel: data.status_label,
        statusData: statusDataForStore, // ✅ ENVIAR OBJETO COMPLETO
      });
    }
  };

  // Handler para exclusão assíncrona de campanha
  onCampaignDeleted = data => {
    const { campaign_id: campaignId, status, message } = data;

    if (status === 'completed') {
      // Remover campanha do store
      this.app.$store.commit('campaigns/DELETE_CAMPAIGN', campaignId);

      // Emitir evento para componentes interessados
      emitter.emit(BUS_EVENTS.CAMPAIGN_DELETED, { campaignId, success: true });

      // Mostrar notificação de sucesso
      emitter.emit(BUS_EVENTS.SHOW_ALERT, {
        message: message || 'Campanha excluída com sucesso!',
        type: 'success',
      });
    } else if (status === 'error') {
      // Emitir evento de erro
      emitter.emit(BUS_EVENTS.CAMPAIGN_DELETED, {
        campaignId,
        success: false,
        error: message,
      });

      // Mostrar notificação de erro
      emitter.emit(BUS_EVENTS.SHOW_ALERT, {
        message: message || 'Erro ao excluir campanha',
        type: 'error',
      });
    }
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
